import React from 'react';
import { Link } from 'react-router-dom';
import PageSeo from './components/PageSeo';
import { useDarkMode } from './DarkModeContext';
import Footer from './Footer';

function PrivacyPolicy() {
    const { darkMode } = useDarkMode();
    
    return (
        <>
        <PageSeo
            title="Privacy Policy – Merra AI"
            description="Read Merra AI privacy and data policy. Learn how we collect, use, and protect your personal information and content. GDPR compliant."
            path="/privacy-policy"
            keywords="privacy policy, data protection, GDPR, data privacy, Merra privacy"
        />
        <div style={{ 
            maxWidth: '800px', 
            margin: '0 auto', 
            padding: '20px',
            fontFamily: 'Arial, sans-serif',
            lineHeight: '1.6',
            color: darkMode ? '#e0e0e0' : '#333',
            backgroundColor: darkMode ? '#1a1a1a' : 'white',
            minHeight: '100vh'
        }}>
            <div style={{ marginBottom: '30px' }}>
                <Link to="/" style={{ color: darkMode ? '#4A9EFF' : '#1976d2', textDecoration: 'none' }}>
                    ← Back to Home
                </Link>
            </div>

            <h1 style={{ color: darkMode ? '#ffffff' : '#2c3e50', borderBottom: `2px solid ${darkMode ? '#4A9EFF' : '#3498db'}`, paddingBottom: '10px' }}>
                Merra – Privacy & Data Policy
            </h1>
            
            <p style={{ color: darkMode ? '#b0b0b0' : '#666', fontStyle: 'italic' }}>
                Last updated: March 4, 2026
            </p>

            <p>
                Merra ("we," "our," "us") is committed to protecting your privacy. This Privacy Policy explains what information we collect, how we use it, and your rights.
            </p>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>1. Information We Collect</h2>

            <h3 style={{ color: darkMode ? '#e0e0e0' : '#34495e' }}>1.1 Information You Provide</h3>
            <ul>
                <li><strong>Account Information</strong>: name, email, billing details.</li>
                <li><strong>Uploaded Content</strong>: videos, audio, images, text, fonts, brand assets.</li>
                <li><strong>Communications</strong>: if you contact us via email, support, or surveys.</li>
            </ul>

            <h3 style={{ color: darkMode ? '#e0e0e0' : '#34495e' }}>1.2 Information We Collect Automatically</h3>
            <ul>
                <li><strong>Usage Data</strong>: how you interact with the platform (features used, frequency, errors).</li>
                <li><strong>Device Information</strong>: IP address, browser, operating system, device type.</li>
                <li><strong>Cookies & Tracking</strong>: session cookies for login, analytics cookies (e.g. Google Analytics, Amplitude) to improve service.</li>
            </ul>

            <h3 style={{ color: darkMode ? '#e0e0e0' : '#34495e' }}>1.3 Third-Party Sources</h3>
            <ul>
                <li>If you connect social media accounts, we may access limited profile/analytics data with your permission.</li>
            </ul>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>2. How We Use Information</h2>
            <ul>
                <li>To operate and deliver the Service (e.g. generating videos).</li>
                <li>To personalize content and recommendations.</li>
                <li>We may analyze anonymized and aggregated usage data to improve product functionality. We do not use user content to train our own machine learning models.</li>
                <li>To send service notifications (account, billing, product updates).</li>
                <li>To detect, prevent, and address technical issues or misuse.</li>
                <li>To comply with legal obligations.</li>
            </ul>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>3. Legal Basis for Processing (GDPR)</h2>
            <p>We process personal data on these grounds:</p>
            <ul>
                <li><strong>Contract</strong>: to provide the Service you signed up for.</li>
                <li><strong>Consent</strong>: when you agree to optional uses (e.g. marketing emails, data for AI training).</li>
                <li><strong>Legitimate Interest</strong>: to improve our service and protect from abuse.</li>
                <li><strong>Legal Obligation</strong>: when required by law.</li>
            </ul>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>4. Data Ownership & Control</h2>
            <ul>
                <li>You <strong>own all content</strong> you upload (videos, images, text).</li>
                <li>You grant us a limited license to process, store, and transform it solely for the purpose of providing the Service.</li>
                <li>You may request deletion of your account and content at any time.</li>
            </ul>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>5. Data Sharing & Third Parties</h2>
            <p>We do not sell your personal data. We do not use user-submitted content for advertising profiling or behavioral tracking.</p>
            <p>We may share data with:</p>
            <ul>
                <li><strong>Service Providers</strong>: cloud hosting (AWS, Vercel), AI model APIs, including OpenAI (GPT models), Google Gemini, and ElevenLabs for voice synthesis, payment processors (Stripe).</li>
                <li><strong>Analytics/Support Tools</strong>: Google Analytics, Amplitude, Intercom.</li>
                <li><strong>Legal Authorities</strong>: if required by law, regulation, or court order.</li>
            </ul>
            <p>Third-party providers process data solely on our behalf and under contractual data protection obligations.</p>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>5a. AI Processing</h2>
            <p>To provide script generation, voice synthesis, and automated editing features, user-submitted content may be processed by third-party AI providers. This includes:</p>
            <ul>
                <li>Text inputs processed by OpenAI or Google Gemini</li>
                <li>Voice recordings processed by ElevenLabs</li>
                <li>Video files processed on our infrastructure using FFmpeg</li>
            </ul>
            <p>These providers act as data processors and process content solely for the purpose of generating the requested output. We do not sell user data and do not authorize third parties to use user content for independent commercial purposes. We may automatically analyze generated content to detect abuse, spam, or violations of our Terms of Service.</p>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>6. Data Storage & Security</h2>
            <ul>
                <li>Data is stored on secure servers located in the EU/US (depending on provider).</li>
                <li>We use encryption in transit (HTTPS/TLS) and at rest where supported.</li>
                <li>Access to personal data is restricted to authorized personnel only.</li>
                <li>No system is 100% secure, but we take reasonable steps to protect your data.</li>
            </ul>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>7. Retention</h2>
            <ul>
                <li>We keep account data while your account is active.</li>
                <li>We may retain minimal transaction/usage data for legal, tax, or auditing purposes after deletion.</li>
                <li>Uploaded content is retained only as long as necessary to provide the Service. Users may delete content at any time, after which it is permanently removed from active systems within a reasonable timeframe.</li>
            </ul>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>8. User Rights</h2>
            <p>Depending on your location (e.g. GDPR, CCPA), you have rights to:</p>
            <ul>
                <li>Access your personal data.</li>
                <li>Correct inaccuracies.</li>
                <li>Request deletion ("right to be forgotten").</li>
                <li>Restrict or object to processing.</li>
                <li>Data portability (receive your data in machine-readable format).</li>
                <li>Withdraw consent at any time.</li>
            </ul>
            <p>Requests can be made at <strong><a href="mailto:hello@merra.ai" style={{ color: darkMode ? '#4A9EFF' : '#1976d2' }}>hello@merra.ai</a></strong>.</p>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>9. Children's Privacy</h2>
            <p>Merra is not directed to individuals under 16. We do not knowingly collect their data. If you believe we have, contact us for deletion.</p>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>10. International Data Transfers</h2>
            <p>If data is transferred outside the EU/EEA, we ensure safeguards (e.g. Standard Contractual Clauses, adequacy decisions).</p>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>11. Cookies & Tracking</h2>
            <ul>
                <li><strong>Necessary cookies</strong>: for login/session.</li>
                <li><strong>Analytics cookies</strong>: to understand usage patterns.</li>
                <li><strong>Marketing cookies</strong>: only with your consent.</li>
            </ul>
            <p>You can manage cookies through your browser settings.</p>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>12. Changes to Policy</h2>
            <p>We may update this Privacy Policy from time to time. Material changes will be notified via email or dashboard.</p>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>13. Contact</h2>
            <p>If you have questions or requests:</p>
            <p>📧 <strong><a href="mailto:hello@merra.ai" style={{ color: darkMode ? '#4A9EFF' : '#1976d2' }}>hello@merra.ai</a></strong></p>
            <Footer />
        </div>
        </>
    );
}

export default PrivacyPolicy;
